import type {
  ConversationDetail,
  ConversationListItem,
  LiveConversationStateT,
  MetricsResponse,
} from '@chatbot/shared-types';
import { request } from './peticion.js';

export interface ListConversationsParams {
  tenantId: string;
  state?: LiveConversationStateT;
  limit?: number;
  cursor?: string;
}

export interface ListConversationsResponse {
  items: ConversationListItem[];
  cursor: string | null;
}

export interface SendMessageResponse {
  ok: boolean;
  messageId: string;
  broadcast: {
    delivered: number;
    stale: number;
    failed: number;
  };
}

export const conversationsApi = {
  list: (params: ListConversationsParams) => {
    const searchParams = new URLSearchParams();
    searchParams.set('tenantId', params.tenantId);
    if (params.state) searchParams.set('state', params.state);
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.cursor) searchParams.set('cursor', params.cursor);

    return request<ListConversationsResponse>(`/admin/conversations?${searchParams.toString()}`);
  },

  get: (tenantId: string, userId: string) => {
    return request<ConversationDetail>(`/admin/conversations/${tenantId}/${userId}`);
  },

  take: (tenantId: string, userId: string) => {
    return request<{ ok: boolean }>(`/admin/conversations/${tenantId}/${userId}/take`, {
      method: 'POST',
    });
  },

  release: (tenantId: string, userId: string) => {
    return request<{ ok: boolean }>(`/admin/conversations/${tenantId}/${userId}/release`, {
      method: 'POST',
    });
  },

  resolve: (tenantId: string, userId: string) => {
    return request<{ ok: boolean }>(`/admin/conversations/${tenantId}/${userId}/resolve`, {
      method: 'POST',
    });
  },

  returnToBot: (tenantId: string, userId: string) => {
    return request<{ ok: boolean }>(`/admin/conversations/${tenantId}/${userId}/return-to-bot`, {
      method: 'POST',
    });
  },

  sendMessage: (tenantId: string, userId: string, text: string) => {
    return request<SendMessageResponse>(`/admin/conversations/${tenantId}/${userId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
  },

  getMetrics: (tenantId: string, from?: string, to?: string) => {
    const searchParams = new URLSearchParams();
    searchParams.set('tenantId', tenantId);
    if (from) searchParams.set('from', from);
    if (to) searchParams.set('to', to);
    return request<MetricsResponse>(`/admin/conversations/metrics?${searchParams.toString()}`);
  },
};
